import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";
import type { BelegKandidat, Vertrag } from "../lib/lieferantVertraege";

/**
 * Lieferantenverträge (Backend: hetzner/webhook/kontor_vertraege_api.py).
 * Jede Mutation invalidiert die Liste — der Server rechnet Status und Ampel,
 * deshalb ist Neuladen der einzige Weg zu einem korrekten Stand.
 */

const KEY = ["lieferant-vertraege"] as const;
const BASE = "/api/kontor/vertraege";

export function useVertraege() {
  return useQuery<Vertrag[]>({
    queryKey: [...KEY],
    queryFn: () => apiGet<Vertrag[]>(BASE),
    staleTime: 30_000,
  });
}

export function useBelegKandidaten(vertragId: string | null) {
  return useQuery<BelegKandidat[]>({
    queryKey: [...KEY, "kandidaten", vertragId],
    queryFn: () => apiGet<BelegKandidat[]>(`${BASE}/${vertragId}/beleg-kandidaten`),
    enabled: !!vertragId,
  });
}

function useInvalidierend<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface VertragCreateBody {
  lieferant_firma_id: string;
  bezeichnung: string;
  kennung: string;
  pruefart: string;
  rhythmus: string;
  soll_brutto: number | null;
  zahlweise: string;
  beginn: string | null;
  ende: string | null;
  kuendigungsfrist: string;
  notizen: string;
}

export function useVertragAnlegen() {
  return useInvalidierend((body: VertragCreateBody) => apiPost(BASE, body));
}

export function useVertragAendern() {
  return useInvalidierend(
    ({ id, ...rest }: Partial<VertragCreateBody> & { id: string; status?: string }) =>
      apiPatch(`${BASE}/${id}`, rest),
  );
}

export interface PeriodeCreateBody {
  vertragId: string;
  periode: string;
  faelligkeit: string | null;
  soll_brutto: number | null;
}

export function usePeriodeAnlegen() {
  return useInvalidierend(({ vertragId, ...rest }: PeriodeCreateBody) =>
    apiPost(`${BASE}/${vertragId}/perioden`, rest),
  );
}

export function usePeriodeAendern() {
  return useInvalidierend(
    ({ periodeId, ...rest }: { periodeId: string; beleg_id?: string; notiz?: string }) =>
      apiPatch(`${BASE}/perioden/${periodeId}`, rest),
  );
}

export function usePeriodeLoeschen() {
  return useInvalidierend((periodeId: string) =>
    apiDelete(`${BASE}/perioden/${periodeId}`),
  );
}

/** Beleg als Vertragsunterlage an den Vertrag — raus aus der Belegverarbeitung. */
export function useUnterlageZuordnen() {
  return useInvalidierend(({ vertragId, belegId }: { vertragId: string; belegId: string }) =>
    apiPost(`${BASE}/${vertragId}/unterlagen`, { beleg_id: belegId }),
  );
}

/** Rückweg: die Unterlage wird wieder ein Beleg-Entwurf. */
export function useUnterlageLoesen() {
  return useInvalidierend(({ vertragId, belegId }: { vertragId: string; belegId: string }) =>
    apiDelete(`${BASE}/${vertragId}/unterlagen/${belegId}`),
  );
}
